import { Card, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddEditPlan from "./AddConfigurePlans";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import { toast } from "react-toastify";

const PlanList = () => {
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);
  const [planToEdit, setPlanToEdit] = useState<any | null>(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef<string>("");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAtFormatted");
  const [loading, setLoading] = useState<boolean>(false);
  const sortFieldMap: Record<string, string> = {
    createdAtFormatted: "createdAt",
    planName: "planName",
    amount: "amount",
    frequency: "frequency",
    revenueRange: "revenueRange",
    status: "status",
  };

  // Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const columns = [
    {
      id: "planName",
      label: "Plan Name",
      minWidth: 150,
      sortable: true,
    },
    {
      id: "amount",
      label: "Amount",
      minWidth: 100,
      sortable: true,
    },
    // {
    //   id: "frequency",
    //   label: "Frequency",
    //   minWidth: 120,
    //   sortable: true,
    // },
    {
      id: "revenueRange",
      label: "Turnover Range",
      minWidth: 200,
      sortable: true,
    },
    // {
    //   id: "status",
    //   label: "Status",
    //   minWidth: 100,
    //   sortable: true,
    // },
    {
      id: "action",
      label: "Action",
      minWidth: 120,
      align: "center" as any,
    },
  ];

  useEffect(() => {
    fetchPlans(searchRef.current);
  }, [currentPage, sortField, sortOrder]);

  // Load Plans
  const fetchPlans = (keyword = "") => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());

    // Status filter

    // Keyword search
    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }

    // Sorting
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);
    WebService.getAPI({
      action: `plan/get-plan?${params.toString()}`,
    })
      .then((res: any) => {
        const perm = res?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        if (!res?.data) return;

        const formattedRows = res.data.map((item: any) => ({
          ...item,
          planName: item.planName,
          amount: `₹${item.amount}`,
          // frequency: item.frequency,
          revenueRange: item.companyRevenueRange
            ?.map((r: any) => r.name)
            .join(", "),
          // status: item.status,
          action: (
            <div>
              {perm.canEdit && (
                <PencilFill
                  className="me-3 cursor-pointer"
                  onClick={() => {
                    setPlanToEdit(item);
                    setShowAddPlan(true);
                  }}
                  title="Edit"
                />
              )}
              {perm.canDelete && (
                <TrashFill
                  color="#DC143C"
                  className="cursor-pointer"
                  onClick={() => handleDeletePlan(item._id)}
                  title="Delete"
                />
              )}
            </div>
          ),
        }));

        setPlans(formattedRows);
        setTotalCount(res?.pagination?.total || formattedRows.length);
      })
      .catch((err: any) => {
        console.error("Error fetching plans:", err);
      });
  };

  // Open delete modal
  const handleDeletePlan = (id: string) => {
    setPlanToDelete(id);
    setShowDeleteModal(true);
  };
  const handleReorder = (newRows: any) => {
    setPlans(newRows);

    const reorderedPayload = newRows.map((item: any, index: number) => ({
      _id: item._id,
      order_by: index + 1,
    }));

    WebService.postAPI({
      action: "plan/update-order",
      body: reorderedPayload,
    })
      .then(() => {})
      .catch(() => {
        toast.error("Failed to update order");
      });
  };
  return (
    <>
      {/* ADD/EDIT OFFCANVAS */}
      <AddEditPlan
        show={showAddPlan}
        handleClose={() => {
          setShowAddPlan(false);
          setPlanToEdit(null);
          fetchPlans(searchRef.current);
        }}
        data={planToEdit}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (planToDelete) {
            WebService.deleteAPI({
              action: `plan/delete-plan/${planToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              fetchPlans(searchRef.current);
            });
          }
        }}
        message="Are you sure you want to delete this plan?"
      />

      {/* MAIN PAGE UI */}
      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between">
            <div className="page-title">Configure Plans</div>
            {pagePermission.canAdd && (
              <CustomButton
                isLoading={false}
                label="Add"
                onClick={() => {
                  setPlanToEdit(null);
                  setShowAddPlan(true);
                }}
                style={{ width: 100 }}
              />
            )}
          </div>

          <div className="row">
            <div className="col-md-3">
              <Form.Label className="label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter plan name or amount"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  searchRef.current = value;                  
                  setSearch(value);
                  setCurrentPage(1);
                  fetchPlans(searchRef.current);
                }}
              />
            </div>
          </div>
          {/* TABLE */}
          <CustomTableSortable
            columns={columns}
            rows={plans}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            isLoading={loading}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
              // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
            }}
                   draggable // 👈 enable drag
            onRowsReorder={handleReorder}
          />
        </Card>
      </div>
    </>
  );
};

export default PlanList;
